import { KSCRUM_LABELS, LABELS } from '~/services/cfg'
import { updateAssignees } from '~/api/issues'
import {
  createSeed,
  getSeed,
  updateDescription,
  updateLabels,
  updateTitle,
} from '..'
import {
  extractIssueDataFromDesc,
  getUpdatedDescription,
} from '~/api/issues/mapping'
import { sprintsHub } from '~/api/milestones/models/SprintsHub'
import { updateIssueState } from '~/api/issues'
import { seedsHub } from './SeedsHub'
import { WithDebounce } from '~/api/common/models/WithDebounce'
import dayjs from 'dayjs'

export const clusters = [
  'internal efficiency',
  'customer experience',
  'tech/other',
] as const

export type Cluster = (typeof clusters)[number]

export const seedStatuses = [
  'TBD',
  'Under Definition',
  'Exploration',
  'Defined',
  'Blocked',
  'Closed',
] as const

export type Status = (typeof seedStatuses)[number]
// one day we could need 'Closed' status
export type SeedCustomData = {
  sto?: number
  okrImpacts: string
  qualitativeBizImpacts: string
  communicationPlan: string
  dueDateNotes: string
  impactAchievementEval: string
  notes: string
  isLongRunning?: boolean
}

export type NewSeedData = SeedCustomData & {
  title: string
  cluster: Cluster
}

const initCustomData = (): SeedCustomData => ({
  okrImpacts: '',
  qualitativeBizImpacts: '',
  communicationPlan: '',
  dueDateNotes: '',
  impactAchievementEval: '',
  notes: '',
})

const {
  intEfficiency,
  custExperience,
  techOrOther,
  exploration,
  underDefinition,
  defined,
  blocked,
} = LABELS

const clusterMap: Record<string, Cluster> = {
  [intEfficiency]: 'internal efficiency',
  [custExperience]: 'customer experience',
  [techOrOther]: 'tech/other',
}

const statusMap: Record<string, Exclude<Status, 'Closed'>> = {
  [defined]: 'Defined',
  [underDefinition]: 'Under Definition',
  [exploration]: 'Exploration',
  [blocked]: 'Blocked',
}

export const getClusterLabel = (cluster: Cluster) =>
  Object.keys(clusterMap).find((k) => clusterMap[k] === cluster)!

const mapLabels = <T>(labels: string[], map: Record<string, T>) => {
  for (const label of labels) {
    if (map[label]) return map[label as keyof typeof map]!
  }
}

const extractCustomData = (desc?: string) =>
  extractIssueDataFromDesc(initCustomData, desc)

// Issue loads seeds as for tech issues (then also blocked!) => private ids => seedsHub.seeds.filter(s => ids.includes(s.iid))
// seed.issues => sprintsHub.sprintsAndUnplanned.flatMap(s => s.issues).filter(i => i.seedIid === seed.iid)
@store()
export class Seed extends WithDebounce {
  constructor(private _fromApi: GLIssue) {
    super()
  }
  static initDraftSeed(): NewSeedData {
    return {
      title: '',
      cluster: 'internal efficiency',
      ...initCustomData(),
    }
  }
  static async createSeed(draftSeed: NewSeedData) {
    const { cluster, title, ...customData } = draftSeed
    const seedIssue = await createSeed(
      draftSeed.title,
      getUpdatedDescription(customData),
      [getClusterLabel(draftSeed.cluster)],
    )
    const seed = new Seed(seedIssue)
    await seedsHub.addSeed(seed)
    return seed
  }

  private get customData(): SeedCustomData {
    return extractCustomData(this._fromApi.description)
  }
  private async setCustomData<TField extends keyof SeedCustomData>(
    field: TField,
    value: SeedCustomData[TField],
    debounced = false,
  ) {
    const updated = await this.queue(
      'setCustomData',
      async () => {
        // get the last version of the seed to avoid conflicts
        const last = await getSeed(this.iid)
        const lastCustomData = extractCustomData(last.description)
        // selectively override just the field we want to update
        lastCustomData[field] = value

        return updateDescription(
          this.iid,
          getUpdatedDescription(lastCustomData, last.description),
        )
      },
      debounced,
    )
    if (updated) this._fromApi.description = updated.description
  }
  /**
   * given a collection of possible values and selected values, returns the updated labels
   * removing non selected possible values and adding selected values
   * ensures that just selected labels per group (possibleLabels) are present
   * @param possibleValues
   * @param selected
   */
  private async updateLabels<T extends string>(
    map: Record<string, T>,
    selected: readonly T[],
  ) {
    const possibleValues = Object.keys(map)
    const labels = this._fromApi.labels.filter(
      (l) => !possibleValues.includes(l),
    )
    const newLabels = selected.map(
      (s) => Object.keys(map).find((k) => map[k] === s)!,
    )
    labels.push(...newLabels)
    // optimistic update
    this._fromApi.labels = labels
    const updated = await updateLabels(this.iid, labels)
    this._fromApi.labels = updated.labels
  }

  private async addLabel(label: string) {
    const labels = this._fromApi.labels
    if (labels.includes(label)) return
    labels.push(label)
    this._fromApi.labels = labels
    const updated = await updateLabels(this.iid, labels)
    this._fromApi.labels = updated.labels
  }

  get iid() {
    return this._fromApi.iid
  }
  get webUrl() {
    return this._fromApi.web_url
  }
  get title() {
    return this._fromApi.title
  }
  async setTitle(title: string, debounced = false) {
    const updated = await this.queue(
      'setTitle',
      async () => {
        this._fromApi.title = title
        return updateTitle(this.iid, title)
      },
      debounced,
    )
    if (updated) this._fromApi.title = updated.title
  }

  get assignees() {
    return this._fromApi.assignees
  }

  async setAssignees(assigneeIds: number[]) {
    const promises: [Promise<GLIssue>, Promise<void>?] = [
      updateAssignees(this._fromApi.project_id, this.iid, assigneeIds),
    ]

    if (this.sto && !assigneeIds.includes(this.sto.id)) {
      promises.push(this.setCustomData('sto', undefined))
    }

    const [updated] = await Promise.all(promises)
    this._fromApi.assignees = updated.assignees
  }

  addAssignee(assigneeId: number) {
    return this.setAssignees([...this.assignees.map((a) => a.id), assigneeId])
  }

  get sto() {
    return this.assignees.find((a) => a.id === this.customData.sto)
  }

  setSto(stoId?: number) {
    const promises: Promise<unknown>[] = []
    promises.push(this.setCustomData('sto', stoId))
    if (stoId && !this.assignees.some((a) => a.id === stoId)) {
      promises.push(this.addAssignee(stoId))
    }
    return Promise.all(promises)
  }

  get pastIssues() {
    return (
      sprintsHub.pastIssues?.filter((i) => !!i?.seeds?.findByIid(this.iid)) ??
      []
    )
  }

  get currentSprintIssues() {
    return (
      sprintsHub.currentSprintIssues?.filter(
        (i) => !!i?.seeds?.findByIid(this.iid),
      ) ?? []
    )
  }

  get futureIssues() {
    return (
      sprintsHub.futureIssues?.filter((i) => !!i?.seeds?.findByIid(this.iid)) ??
      []
    )
  }

  get openIssues() {
    return [...this.futureIssues, ...this.currentSprintIssues]
  }

  get issues() {
    return [
      ...this.pastIssues,
      ...this.currentSprintIssues,
      ...this.futureIssues,
    ]
  }

  /**
   * the sum of the effort spent (SP) for all the issues related to this seed
   */
  get effortSpent() {
    return this.issues.sum((i) => i.spentSP)
  }

  /**
   * the sum of the effort remaining (SP) for all the issues related to this seed
   */
  get effortRemaining() {
    return this.openIssues.sum((i) => i.weight)
  }

  /**
   * the total effort (spent + remaining) for all the issues related to this seed
   */
  get totalEffort() {
    return this.effortSpent + this.effortRemaining
  }

  /**
   * the percentage of the total effort spent for all the issues related to this seed
   */
  get progressPerc() {
    if (this.totalEffort === 0) return 0
    return round((100 * this.effortSpent) / this.totalEffort)
  }

  get masterIssues() {
    return this.issues.filter((i) => i.isMaster)
  }

  /**
   * returns the master issues grouped by sprint number
   */
  get masterIssuesBySprint() {
    return this.masterIssues.groupBy((i) => i.sprint.number)
  }

  get latestMasterIssueSprint() {
    // find last sprint by number
    return this.masterIssues
      .flatMap((i) => i.sprint)
      .distinct()
      .sort((a, b) => a.number - b.number)
      .last()
  }

  get latestReleaseDate() {
    if (this.latestMasterIssueSprint?.isUnallocated) return undefined
    const endDate = this.latestMasterIssueSprint?.endDate
    if (endDate) return endDate.toDayMonth()
  }

  // custom data

  /**
   * seed is a long running one if it is not supposed to be closed by a specific date
   */
  get isLongRunning() {
    return !!this.customData.isLongRunning
  }
  setIsLongRunning(value: boolean) {
    return this.setCustomData('isLongRunning', value)
  }
  get okrImpacts() {
    return this.customData.okrImpacts
  }
  setOkrImpacts(value: string, debounced = false) {
    return this.setCustomData('okrImpacts', value, debounced)
  }
  get qualitativeBizImpacts() {
    return this.customData.qualitativeBizImpacts
  }
  setQualitativeBizImpacts(value: string, debounced = false) {
    return this.setCustomData('qualitativeBizImpacts', value, debounced)
  }
  get dueDateNotes() {
    return this.customData.dueDateNotes
  }
  setDueDateNotes(value: string, debounced = false) {
    return this.setCustomData('dueDateNotes', value, debounced)
  }
  get impactAchievementEval() {
    return this.customData.impactAchievementEval
  }
  setImpactAchievementEval(value: string, debounced = false) {
    return this.setCustomData('impactAchievementEval', value, debounced)
  }
  get notes() {
    return this.customData.notes
  }
  setNotes(value: string, debounced = false) {
    return this.setCustomData('notes', value, debounced)
  }
  get communicationPlan() {
    return this.customData.communicationPlan
  }
  setCommunicationPlan(value: string, debounced = false) {
    return this.setCustomData('communicationPlan', value, debounced)
  }
  get cluster(): Cluster {
    return (
      mapLabels(this._fromApi.labels, clusterMap) ?? clusterMap[techOrOther]!
    )
  }
  setCluster(value: Cluster) {
    return this.updateLabels(clusterMap, [value])
  }
  get status(): Status {
    if (this._fromApi.state === 'closed') return 'Closed'
    return mapLabels(this._fromApi.labels, statusMap) ?? 'TBD'
  }
  setStatus(value: Status) {
    if (value === 'Closed') return this.close()

    const updateLabel = this.updateLabels(
      statusMap,
      value === 'TBD' ? [] : [value],
    )
    // if the status is not closed, and the seed is closed, we need to reopen it
    if (this._fromApi.state === 'closed') {
      return Promise.all([updateLabel, this.reopen()])
    }
    return updateLabel
  }

  get closedAt() {
    if (!this._fromApi.closed_at) return
    return dayjs(this._fromApi.closed_at)
  }
  get closedAtQuarter() {
    if (!this.closedAt) return
    // compute the quarter label as "23/Q1"
    const quarter = Math.floor(this.closedAt.month() / 3) + 1
    const year = this.closedAt.year() % 2000
    return `${year}/Q${quarter}`
  }
  get closedAtFormatted() {
    if (!this.closedAt) return
    return this.closedAt.toDateString()
  }

  /**
   * closes the seed issue, marks it as deleted and removes it from the seedsHub
   * all seeds fetches filter out the deleted ones!
   */
  async delete() {
    await Promise.all([this.close(), this.addLabel(KSCRUM_LABELS.deletedSeed)])
    seedsHub.deleteSeed(this)
  }

  async close() {
    if (this._fromApi.state === 'closed') return
    this._fromApi.state = 'closed'
    const updated = await updateIssueState(
      this._fromApi.project_id,
      this._fromApi.iid,
      'close',
    )
    this._fromApi.state = updated.state
  }
  async reopen() {
    if (this._fromApi.state !== 'closed') return
    this._fromApi.state = 'opened'
    const updated = await updateIssueState(
      this._fromApi.project_id,
      this._fromApi.iid,
      'reopen',
    )
    this._fromApi.state = updated.state
  }
}
